<template>
  <div
    v-if="libraryType"
    :class="['library', libraryBrandClass]"
  >
    <div class="library__body">
      <section
        :class="[
          'main-content',
          'main-content--with-hero',
          { 'main-content--hero-collapsed': isHeroCollapsed }
        ]"
      >
        <section
          :class="[
            'library-hero',
            libraryHeroClass,
            { 'library-hero--collapsed': isHeroCollapsed }
          ]"
          :style="libraryHeroStyle"
          role="img"
          :aria-label="`${activeBrandName} ${libraryHeroTitle} banner`"
          :aria-hidden="String(isHeroCollapsed)"
        >
          <div class="library-hero__overlay" />

          <div class="library-hero__content">
            <span class="library-hero__eyebrow">
              {{ activeBrandName }}
            </span>

            <h1 class="library-hero__title">
              {{ libraryHeroTitle }}
            </h1>

            <span class="library-hero__count">
              {{ resultCountText }}
            </span>
          </div>

          <div class="library-hero__statement">
            <span>{{ activeBrandStatement }}</span>
            <span class="library-hero__statement-line" />
          </div>
        </section>

        <div
          :class="[
            'library-page-header',
            'library-page-header--with-hero',
            { 'library-page-header--compact': isHeroCollapsed }
          ]"
        >
          <transition name="library-summary">
            <div
              v-if="isHeroCollapsed"
              class="library-page-header__summary"
            >
              <h1 class="library-page-header__summary-title">
                {{ libraryHeroTitle }}
              </h1>

              <span class="library-page-header__summary-count">
                {{ resultCountText }}
              </span>
            </div>
          </transition>

          <LibraryContentHeader
            class="library-page-header__existing-header"
            :is-loading="isLoading"
            :sorting="sorting"
            :new-item-files="newItemFiles"
            :show-style-bulk-actions-toolbar="showStyleBulkActionsToolbar"
            :selected-style-ids="selectedStyleIds"
            :items-count="paging.itemsCount"
            :are-all-visible-selected="areAllVisibleSelected"
            :filters-open="!isFilterSidebarCollapsed"
            @toggle-style-bulk-actions-toolbar="onToggleStyleBulkActionsToolbar"
            @toggle-select-visible-styles="onToggleSelectVisibleStyles"
            @clear-style-selection="clearSelectedStyleIds"
            @sorting-change="onSortingChange"
            @search-update="fetchData(items, fetchFilteredItems)"
            @toggle-filters="toggleFilterSidebar"
            @bulk-delete="$emit('bulk-delete', $event)"
            @bulk-update-status="$emit('bulk-update-status', $event)"
          />
        </div>

        <div class="main-content__workspace">
          <LibraryItems
            v-if="items"
            ref="libraryItems"
            class="main-content__items"
            :items="items"
            :library-type="libraryType"
            :user="user"
            :loading="isLoading"
            :show-style-bulk-actions-toolbar="showStyleBulkActionsToolbar"
            :selected-style-ids="selectedStyleIds"
            :show-pagination="isPaginationMode"
            :paging="paging"
            @style-selection-change="onStyleSelectionChange"
            @page-change="onPageChange"
            @scroll-hit-bottom="onScrollHitBottom"
            @files-dropped="onFilesDropped"
          />
        </div>
      </section>

      <template v-if="detailId">
        <DetailStyle
          v-if="libraryType === LIBRARY_TYPE.STYLE"
          :item-id="detailId"
          @close="closeDetail"
        />
        <DetailBlock
          v-else-if="libraryType === LIBRARY_TYPE.BLOCK"
          :item-id="detailId"
          @close="closeDetail"
        />
        <DetailItem
          v-else
          :item-id="detailId"
        />
      </template>

      <aside
        id="library-filters-sidebar"
        :class="[
          'library__aside-right-container',
          {
            'library__aside-right-container--collapsed':
              isFilterSidebarCollapsed
          }
        ]"
        :aria-expanded="String(!isFilterSidebarCollapsed)"
        aria-label="Filters"
      >
        <div class="library__aside-right-content">
          <TheAsideRight
            v-show="!isFilterSidebarCollapsed"
            class="library__aside-right"
            @filter-library="onFilterLibrary"
          />
        </div>

        <div class="library__aside-right-footer">
          <button
            type="button"
            class="library__aside-right-toggle"
            :aria-label="
              isFilterSidebarCollapsed ? 'Expand filters' : 'Collapse filters'
            "
            :title="
              isFilterSidebarCollapsed ? 'Expand filters' : 'Collapse filters'
            "
            @click="toggleFilterSidebar"
          >
            <i
              class="the-aside-left__toggle-icon"
              :class="
                isFilterSidebarCollapsed
                  ? 'el-icon-d-arrow-left'
                  : 'el-icon-d-arrow-right'
              "
            />
          </button>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { LIBRARY_TYPE } from '@/constants/libraryType'
import { FetchData } from '@/mixins/fetchData.js'
import Utils from '@/services/utils'
import { getFormConfig } from '@/services/formUtils'
import DetailItem from '@/components/library/DetailItem'
import LibraryContentHeader from '@/components/library/LibraryContentHeader'

import ckBannerImage from '@/assets/images/ck.png'
import thBannerImage from '@/assets/images/th.png'
import libraryBannerImage from '@/assets/images/library-banner.png'

const INFINITE_SCROLL_PAGE_SIZE = 10
const BULK_SELECTION_PAGE_SIZE = 50
const HERO_COLLAPSE_SCROLL_THRESHOLD = 12
const HERO_EXPAND_SCROLL_THRESHOLD = 2

const LIBRARY_HERO_TITLES = {
  [LIBRARY_TYPE.BLOCK]: 'Blocks',
  [LIBRARY_TYPE.TRIM]: 'Trims',
  [LIBRARY_TYPE.FABRIC]: 'Fabrics',
  [LIBRARY_TYPE.STITCH]: 'Stitches',
  [LIBRARY_TYPE.COLOR]: 'Color Cards',
  [LIBRARY_TYPE.STYLE]: 'Styles',
  [LIBRARY_TYPE.JOB]: 'My Renders'
}

export default {
  name: 'ItemsLibrary',

  components: {
    DetailItem,
    LibraryContentHeader
  },

  mixins: [FetchData],

  data () {
    return {
      LIBRARY_TYPE,
      detailId: null,
      newItemFiles: null,
      isLoading: false,
      isFilterSidebarCollapsed: true,
      isHeroCollapsed: false,
      libraryScrollElement: null,
      brandBannerImages: {
        CK: ckBannerImage,
        TH: thBannerImage
      }
    }
  },

  computed: {
    ...mapGetters([
      'getItems',
      'getSortingItems',
      'getAvailableFilters',
      'getItemDetail',
      'getPagingItems',
      'getSelectedStyleIds'
    ]),

    ...mapGetters({
      browzwearAPI: 'getBrowzwearAPI',
      user: 'getCognitoUserData',
      libraryType: 'getActiveLibraryType',
      activeTeam: 'getActiveTeam',
      activeBrand: 'getActiveBrand',
      showStyleBulkActionsToolbar: 'getStyleBulkSelectionMode'
    }),

    /**
     * @returns {boolean}
     */
    isStyleLibrary () {
      return this.libraryType === LIBRARY_TYPE.STYLE
    },

    /**
     * @returns {string}
     */
    libraryHeroTitle () {
      if (this.$route.name === 'LibraryStylesWatchlist') {
        return 'My Watchlist'
      }

      return LIBRARY_HERO_TITLES[this.libraryType] || 'Library'
    },

    /**
     * @returns {string}
     */
    activeBrandCode () {
      if (!this.activeBrand) {
        return ''
      }

      return String(
        this.activeBrand.code || this.activeBrand.label || ''
      ).toUpperCase()
    },

    /**
     * @returns {string}
     */
    activeBrandName () {
      if (this.activeBrandCode === 'CK') {
        return 'CALVIN KLEIN'
      }

      if (this.activeBrandCode === 'TH') {
        return 'TOMMY HILFIGER'
      }

      return this.activeBrand && this.activeBrand.name
        ? String(this.activeBrand.name).toUpperCase()
        : 'PVH'
    },

    /**
     * @returns {string}
     */
    activeBrandStatement () {
      return this.activeBrandCode === 'TH'
        ? 'MAKE IT POSSIBLE'
        : 'SIMPLICITY LIVES ON'
    },

    /**
     * @returns {string}
     */
    libraryBrandClass () {
      return this.activeBrandCode
        ? `library--${this.activeBrandCode.toLowerCase()}`
        : ''
    },

    /**
     * @returns {string}
     */
    libraryHeroClass () {
      if (!this.isStyleLibrary) {
        return 'library-hero--shared'
      }

      return this.activeBrandCode
        ? `library-hero--${this.activeBrandCode.toLowerCase()}`
        : ''
    },

    /**
     * @returns {object}
     */
    libraryHeroStyle () {
      const bannerImage = this.isStyleLibrary
        ? this.brandBannerImages[this.activeBrandCode]
        : libraryBannerImage

      return bannerImage
        ? {
            backgroundImage: `url(${bannerImage})`
          }
        : {}
    },

    /**
     * @returns {string}
     */
    resultCountText () {
      const itemCount = this.paging.itemsCount || 0

      return `${itemCount} ${itemCount === 1 ? 'result' : 'results'}`
    },

    /**
     * @returns {object}
     */
    paging () {
      return (
        this.getPagingItems() || {
          currentPage: 1,
          itemsLimit: INFINITE_SCROLL_PAGE_SIZE,
          itemsCount: 0,
          mode: 'infinite'
        }
      )
    },

    /**
     * @returns {object[]}
     */
    items () {
      return this.getItems() || []
    },

    /**
     * @returns {number[]}
     */
    visibleStyleIds () {
      if (this.libraryType !== LIBRARY_TYPE.STYLE) {
        return []
      }

      return this.items
        .filter(item => item.libraryType === LIBRARY_TYPE.STYLE)
        .map(item => item.id)
        .filter(id => id !== null && id !== undefined)
    },

    /**
     * @returns {boolean}
     */
    areAllVisibleSelected () {
      return (
        this.visibleStyleIds.length > 0 &&
        this.visibleStyleIds.every(id => this.selectedStyleIds.includes(id))
      )
    },

    /**
     * @returns {boolean}
     */
    isPaginationMode () {
      return this.paging.mode === 'pagination'
    },

    /**
     * @returns {Array}
     */
    selectedStyleIds () {
      return this.getSelectedStyleIds || []
    },

    /**
     * @returns {object}
     */
    itemDetail () {
      return this.getItemDetail()
    },

    /**
     * @returns {Array}
     */
    sorting () {
      return this.getSortingItems()
    },

    /**
     * @returns {Array}
     */
    availableFilters () {
      return this.getAvailableFilters()
    },

    /**
     * @returns {Array}
     */
    camelCasedFilters () {
      return Utils.convertObjectKeysToCamelCase(this.availableFilters || {})
    },

    /**
     * @returns {object}
     */
    formConfiguration () {
      return getFormConfig(this.libraryType)
    }
  },

  watch: {
    /**
     * @param {string} to
     * @param {string} from
     */
    $route (to, from) {
      if (from.name !== to.name) {
        this.resetHeroCollapse()
        this.updateWorkspace()
      }

      if (from.query.group_id !== to.query.group_id) {
        this.resetHeroCollapse()
        this.fetchAvailableFilters()
      }

      this.parseUrlForDetail()
    },

    /**
     * Re-bind the scroll listener when the list DOM is recreated.
     */
    items () {
      this.$nextTick().then(() => {
        this.bindLibraryScrollListener()
      })
    },

    /**
     * The list scroll element can appear while the initial skeleton is loading.
     */
    isLoading () {
      this.$nextTick().then(() => {
        this.bindLibraryScrollListener()
      })
    },

    /**
     * @param {boolean} isActive
     * @param {boolean} wasActive
     */
    async showStyleBulkActionsToolbar (isActive, wasActive) {
      if (isActive === wasActive) {
        return
      }

      await this.updatePagingModeForSelection(isActive)

      if (!isActive && this.libraryType === LIBRARY_TYPE.STYLE) {
        this.isLoading = true

        try {
          this.resetItems({
            libraryType: this.libraryType,
            resetPage: false,
            resetCount: false
          })

          await this.fetchData(this.items, this.fetchFilteredItems)
        } finally {
          this.isLoading = false
        }
      }
    }
  },

  mounted () {
    this.updateWorkspace()
    this.parseUrlForDetail()

    this.$nextTick().then(() => {
      this.bindLibraryScrollListener()
    })
  },

  beforeDestroy () {
    this.unbindLibraryScrollListener()

    if (this.showStyleBulkActionsToolbar) {
      this.setStyleBulkSelectionMode(false)
      this.clearSelectedStyleIds()
    }
  },

  methods: {
    /**
     * Listen to the card grid scroll container.
     *
     * The hero lives outside the scrolling list, so the parent needs to react
     * to the list scroll position in order to collapse it.
     */
    bindLibraryScrollListener () {
      const libraryItems = this.$refs.libraryItems
      const scrollElement =
        libraryItems &&
        libraryItems.$refs &&
        libraryItems.$refs.itemListScrollable

      if (scrollElement === this.libraryScrollElement) {
        return
      }

      this.unbindLibraryScrollListener()

      if (!scrollElement) {
        return
      }

      this.libraryScrollElement = scrollElement
      this.libraryScrollElement.addEventListener(
        'scroll',
        this.onLibraryScroll,
        { passive: true }
      )

      this.onLibraryScroll()
    },

    /**
     * Remove the listener from the previous list scroll container.
     */
    unbindLibraryScrollListener () {
      if (!this.libraryScrollElement) {
        return
      }

      this.libraryScrollElement.removeEventListener(
        'scroll',
        this.onLibraryScroll
      )
      this.libraryScrollElement = null
    },

    /**
     * Collapse the hero shortly after scrolling starts and restore it only
     * when the user returns to the very top. The two thresholds prevent
     * flickering around the boundary.
     */
    onLibraryScroll () {
      if (!this.libraryScrollElement) {
        return
      }

      const scrollTop = this.libraryScrollElement.scrollTop

      if (!this.isHeroCollapsed && scrollTop > HERO_COLLAPSE_SCROLL_THRESHOLD) {
        this.isHeroCollapsed = true

        return
      }

      if (this.isHeroCollapsed && scrollTop <= HERO_EXPAND_SCROLL_THRESHOLD) {
        this.isHeroCollapsed = false
      }
    },

    /**
     * Restore the full hero for a new library or team.
     */
    resetHeroCollapse () {
      this.isHeroCollapsed = false

      if (this.libraryScrollElement) {
        this.libraryScrollElement.scrollTop = 0
      }
    },

    /**
     * Expands or collapses the right filter sidebar.
     */
    toggleFilterSidebar () {
      this.isFilterSidebarCollapsed = !this.isFilterSidebarCollapsed
    },

    ...mapActions([
      'setActiveLibraryType',
      'setSortingItems',
      'incrementPagingItems',
      'fetchFilteredItems',
      'fetchAvailableFilters',
      'fetchTags',
      'resetItems',
      'setWatched',
      'addSelectedStyleId',
      'removeSelectedStyleId',
      'clearSelectedStyleIds',
      'setPagingItemsPage',
      'setPagingItemsMode',
      'setPagingItemsLimit',
      'setSelectedStyleIds',
      'setStyleBulkSelectionMode'
    ]),

    /**
     * Enable or disable style bulk-selection mode.
     *
     * @param {boolean} isActive
     */
    onToggleStyleBulkActionsToolbar (isActive) {
      if (this.libraryType !== LIBRARY_TYPE.STYLE) {
        return
      }

      this.setStyleBulkSelectionMode(isActive)
    },

    /**
     * Switch between infinite scrolling and paginated bulk-selection mode
     * without refreshing the styles.
     *
     * @param {boolean} isActive
     */
    async updatePagingModeForSelection (isActive) {
      if (this.libraryType !== LIBRARY_TYPE.STYLE) {
        return
      }

      this.setPagingItemsMode({
        mode: isActive ? 'pagination' : 'infinite',
        libraryType: this.libraryType
      })

      this.setPagingItemsLimit({
        itemsLimit: isActive
          ? BULK_SELECTION_PAGE_SIZE
          : INFINITE_SCROLL_PAGE_SIZE,
        libraryType: this.libraryType
      })

      this.setPagingItemsPage({
        currentPage: 1,
        libraryType: this.libraryType
      })

      if (!isActive) {
        this.clearSelectedStyleIds()

        return
      }

      this.isLoading = true

      try {
        this.resetItems({
          libraryType: this.libraryType,
          resetPage: false,
          resetCount: false
        })

        await this.fetchData(this.items, this.fetchFilteredItems)
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Select or deselect every style visible on the current page.
     *
     * @param {boolean} selected
     */
    onToggleSelectVisibleStyles (selected) {
      if (selected) {
        const mergedIds = [
          ...new Set([...this.selectedStyleIds, ...this.visibleStyleIds])
        ]

        this.setSelectedStyleIds(mergedIds)

        return
      }

      const visibleIds = new Set(this.visibleStyleIds)

      this.setSelectedStyleIds(
        this.selectedStyleIds.filter(id => !visibleIds.has(id))
      )
    },

    /**
     * Load a specific pagination page.
     *
     * @param   {number}        currentPage
     *
     * @returns {Promise<void>}
     */
    async onPageChange (currentPage) {
      if (!this.isPaginationMode || currentPage === this.paging.currentPage) {
        return
      }

      this.isLoading = true

      this.setPagingItemsPage({
        currentPage,
        libraryType: this.libraryType
      })

      this.resetItems({
        libraryType: this.libraryType,
        resetPage: false,
        resetCount: false
      })

      try {
        await this.fetchData(this.items, this.fetchFilteredItems)
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Add or remove one style from the shared selection.
     *
     * @param {object}  selection
     * @param {number}  selection.id
     * @param {boolean} selection.selected
     */
    onStyleSelectionChange ({ id, selected }) {
      if (!this.showStyleBulkActionsToolbar) {
        return
      }

      if (selected) {
        this.addSelectedStyleId(id)

        return
      }

      this.removeSelectedStyleId(id)
    },

    /**
     */
    async updateWorkspace () {
      this.isLoading = true

      const newLibraryType = this.$router.currentRoute.meta.libraryType

      if (this.$route.name === 'LibraryStylesWatchlist') {
        this.setWatched({
          watched: true,
          libraryType: LIBRARY_TYPE.STYLE
        })
      } else {
        this.setWatched({
          watched: false,
          libraryType: LIBRARY_TYPE.STYLE
        })
      }

      if (
        newLibraryType !== LIBRARY_TYPE.STYLE &&
        this.showStyleBulkActionsToolbar
      ) {
        this.setStyleBulkSelectionMode(false)
        this.clearSelectedStyleIds()
      }

      this.setActiveLibraryType(newLibraryType)

      const shouldUsePagination =
        newLibraryType === LIBRARY_TYPE.STYLE &&
        this.showStyleBulkActionsToolbar

      this.setPagingItemsMode({
        mode: shouldUsePagination ? 'pagination' : 'infinite',
        libraryType: newLibraryType
      })

      this.setPagingItemsLimit({
        itemsLimit: shouldUsePagination
          ? BULK_SELECTION_PAGE_SIZE
          : INFINITE_SCROLL_PAGE_SIZE,
        libraryType: newLibraryType
      })

      this.setPagingItemsPage({
        currentPage: 1,
        libraryType: newLibraryType
      })

      this.resetItems({
        libraryType: newLibraryType
      })

      try {
        await this.fetchFilters()

        await this.fetchData(this.items, this.fetchFilteredItems)
      } finally {
        this.isLoading = false
      }

      if (this.libraryType === LIBRARY_TYPE.STYLE) {
        this.fetchTags({
          assignedOnly: true
        })
      }
    },

    /**
     */
    async fetchFilters () {
      if (!this.availableFilters) {
        await this.fetchAvailableFilters()
      }
    },

    /**
     * Apply filters and clear selections that may no longer be visible.
     *
     * @returns {Promise<void>}
     */
    async onFilterLibrary () {
      if (this.showStyleBulkActionsToolbar) {
        this.clearSelectedStyleIds()

        this.setPagingItemsPage({
          currentPage: 1,
          libraryType: this.libraryType
        })
      }

      this.resetItems({
        libraryType: this.libraryType,
        resetPage: !this.showStyleBulkActionsToolbar,
        resetCount: true
      })

      await this.fetchData(this.items, this.fetchFilteredItems)
    },

    /**
     */
    closeDetail () {
      if (!this.detailId) {
        // avoid duplicated navigation
        return
      }

      this.detailId = null
      const newQueryUrl = JSON.parse(JSON.stringify(this.$route.query))
      delete newQueryUrl.detail

      this.$router.replace({
        query: newQueryUrl
      })
    },

    /**
     */
    parseUrlForDetail () {
      this.detailId = this.$route.query.detail
        ? parseInt(this.$route.query.detail)
        : null
    },

    /**
     * @param {string} changedSorting
     */
    onSortingChange (changedSorting) {
      if (this.sorting === changedSorting) {
        return
      }

      this.setSortingItems({
        sorting: changedSorting
      })

      if (this.showStyleBulkActionsToolbar) {
        this.clearSelectedStyleIds()

        this.setPagingItemsPage({
          currentPage: 1,
          libraryType: this.libraryType
        })
      }

      this.fetchData(this.items, this.fetchFilteredItems, true)
    },

    /**
     */
    onScrollHitBottom () {
      if (this.isPaginationMode || this.isLoading) {
        return
      }

      this.incrementPagingItems()

      this.fetchData(this.items, this.fetchFilteredItems, true)
    },

    /**
     * Store files dropped onto the style library.
     *
     * @param {FileList|File[]} files
     */
    onFilesDropped (files) {
      if (this.showStyleBulkActionsToolbar) {
        return
      }

      this.newItemFiles = files
    }
  }
}
</script>

<style lang="scss" scoped>
$root-height: percentage(100);
$library-surface: $white;
$library-page-background: #f7f7f5;
$library-text: #171819;
$library-muted-text: #6f7378;
$library-soft-control: #f2f2f0;
$library-border-color: #deded9;
$library-border-width: 1px;
$library-hero-height: spacing(21);
$library-hero-title-size: spacing(7);
$library-hero-collapse-motion: 220ms cubic-bezier(0.2, 0.8, 0.2, 1);
$library-page-header-z-index: 8;
$library-compact-title-size: spacing(2.5);
$library-compact-summary-gap: spacing(1);
$library-compact-summary-padding: spacing(2);
$library-content-horizontal-padding: spacing(5);
$library-control-height: spacing(4.5);
$library-control-radius: spacing(0.8);
$library-hero-content-z-index: 1;
$library-th-accent: #00174f;
$library-filter-button-active-shadow: 0 spacing(0.5) spacing(1.5)
  rgba($blue, 0.22);

$filter-sidebar-z-index: 9;
$filter-sidebar-expanded-width: spacing(27);
$filter-sidebar-collapsed-width: spacing(9.5);
$filter-sidebar-transition-duration: 0.25s;
$filter-sidebar-footer-height: spacing(7);
$filter-sidebar-toggle-size: spacing(4);
$filter-sidebar-toggle-edge-offset: spacing(2);
$filter-sidebar-toggle-bottom-offset: spacing(1.75);
$filter-sidebar-toggle-color: #15171a;
$filter-sidebar-toggle-muted-color: #71767c;
$filter-sidebar-toggle-hover-background: #f5f5f3;
$filter-sidebar-toggle-chevron-size: spacing(1);
$filter-sidebar-toggle-chevron-border-width: 2px;
$filter-sidebar-toggle-focus-shadow: 0 0 0 2px rgba(21, 23, 26, 0.12);
$filter-sidebar-active-background: #f1f1ef;
$filter-sidebar-active-indicator-width: spacing(0.5);

.library {
  display: flex;
  flex-direction: column;
  width: percentage(100);
  height: $root-height;
  min-height: 0;
  color: $library-text;
  background-color: $library-page-background;
}

.library__body {
  display: flex;
  flex: 1 1 auto;
  width: percentage(100);
  min-width: 0;
  min-height: 0;
  background-color: $library-page-background;
}

.main-content {
  position: relative;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
  height: percentage(100);
  min-height: 0;
  background-color: $library-page-background;
}

.library-hero {
  position: relative;
  display: flex;
  flex: 0 0 $library-hero-height;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  width: percentage(100);
  height: $library-hero-height;
  overflow: hidden;
  background-color: $library-soft-control;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  transform: translateY(0);
  opacity: 1;
  transition:
    flex-basis $library-hero-collapse-motion,
    height $library-hero-collapse-motion,
    opacity $library-hero-collapse-motion,
    transform $library-hero-collapse-motion;
}

.library-hero--collapsed {
  flex-basis: 0;
  height: 0;
  min-height: 0;
  transform: translateY(spacing(-1));
  opacity: 0;
  pointer-events: none;
}

.library-hero__overlay {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(
      90deg,
      rgba($white, 0.98) 0%,
      rgba($white, 0.96) 28%,
      rgba($white, 0.75) 47%,
      rgba($white, 0.12) 72%,
      transparent 100%
    );
  pointer-events: none;
}

.library-hero--ck {
  background-position: center 34%;
  filter: grayscale(1);
}

.library-hero--th {
  background-position: center 28%;
}

.library-hero--shared {
  background-position: center 52%;
}

.library-hero--th .library-hero__overlay {
  background:
    linear-gradient(
      90deg,
      rgba($white, 0.98) 0%,
      rgba($white, 0.94) 31%,
      rgba($white, 0.68) 49%,
      rgba($white, 0.08) 76%,
      transparent 100%
    );
}

.library-hero__content {
  position: relative;
  z-index: $library-hero-content-z-index;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-left: $library-content-horizontal-padding;
}

.library-hero__eyebrow {
  margin-bottom: spacing(1.5);
  color: $library-muted-text;
  font-weight: $font-weight-medium;
  font-size: spacing(1.25);
  line-height: 1;
  letter-spacing: spacing(0.35);
}

.library-hero__title {
  margin: 0;
  color: $library-text;
  font-weight: $font-weight-regular;
  font-size: $library-hero-title-size;
  line-height: 0.95;
  letter-spacing: spacing(-0.12);
}

.library-hero__count {
  margin-top: spacing(2);
  color: $library-text;
  font-size: spacing(1.4);
  line-height: 1;
}

.library-hero__statement {
  position: relative;
  z-index: $library-hero-content-z-index;
  display: flex;
  flex-direction: column;
  gap: spacing(1.25);
  align-items: flex-start;
  width: spacing(18);
  margin-right: percentage(42);
  color: $library-text;
  font-weight: $font-weight-medium;
  font-size: spacing(1.3);
  line-height: 1.55;
  letter-spacing: spacing(0.28);
}

.library-hero__statement-line {
  display: block;
  width: spacing(4.5);
  height: $library-border-width;
  background-color: $library-text;
}

.library-page-header {
  position: sticky;
  top: 0;
  z-index: $library-page-header-z-index;
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  box-sizing: border-box;
  width: percentage(100);
  background-color: $library-surface;
  border-bottom: $library-border-width solid $library-border-color;
}

.library-page-header--with-hero {
  box-shadow: 0 spacing(0.25) spacing(1.5) rgba($black, 0.03);
}

.library-page-header--compact {
  box-shadow: 0 spacing(0.5) spacing(2) rgba($black, 0.08);
}

.library-page-header__summary {
  display: flex;
  flex: 0 0 auto;
  gap: $library-compact-summary-gap;
  align-items: baseline;
  min-width: 0;
  padding-left: $library-compact-summary-padding;
  white-space: nowrap;
}

.library-page-header__summary-title {
  margin: 0;
  color: $library-text;
  font-weight: $font-weight-semibold;
  font-size: $library-compact-title-size;
  line-height: 1;
}

.library-page-header__summary-count {
  color: $library-muted-text;
  font-size: spacing(1.25);
  line-height: 1;
}

.library-page-header__existing-header {
  flex: 1 1 auto;
  min-width: 0;
}

.library-summary-enter-active,
.library-summary-leave-active {
  transition:
    opacity $library-hero-collapse-motion,
    transform $library-hero-collapse-motion;
}

.library-summary-enter,
.library-summary-leave-to {
  transform: translateX(spacing(-1));
  opacity: 0;
}

/deep/ .library-content-header {
  box-sizing: border-box;
  width: percentage(100);
  margin: 0;
  padding: spacing(2);
  background-color: $library-surface;
  transition: padding $library-hero-collapse-motion;
}

.library-page-header--compact /deep/ .library-content-header {
  padding-left: spacing(1.5);
}

/deep/ .library-content-header__top-row {
  align-items: center;
  width: percentage(100);
  margin: 0;
}

/deep/ .library-content-header__title-group {
  display: none;
}

/deep/ .library-content-header__wrapper-options {
  display: flex;
  flex: 1 1 auto;
  width: percentage(100);
  min-width: 0;
}

/deep/ .library-content-header__inputs {
  display: flex;
  flex: 1 1 auto;
  gap: spacing(1);
  align-items: center;
  justify-content: flex-end;
  width: percentage(100);
  min-width: 0;
}

/deep/ .library-content-header__inputs > *,
/deep/ .library-content-header__actions > * {
  margin-right: 0;
}

/deep/ .library-content-header__search {
  flex: 0 1 spacing(38);
  width: spacing(38);
  min-width: spacing(24);
  max-width: spacing(38);
  margin-right: auto;
}

/deep/ .library-content-header__actions {
  display: flex;
  flex: 0 0 auto;
  gap: spacing(1);
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
}

/deep/ .library-content-header__select {
  flex: 0 0 spacing(20);
  max-width: spacing(20);
  margin-right: 0;
}

/deep/ .library-content-header__search .el-input__inner,
/deep/ .library-content-header__select .el-input__inner {
  height: $library-control-height;
  color: $library-text;
  background-color: $library-surface;
  border-color: $library-border-color;
  border-radius: $library-control-radius;
}

/deep/ .library-content-header__inputs .el-button {
  height: $library-control-height;
  padding: 0 spacing(1.75);
  color: $library-text;
  font-weight: $font-weight-medium;
  background-color: $library-surface;
  border-color: $library-border-color;
  border-radius: $library-control-radius;
}

/deep/ .library-content-header__inputs .el-button:hover,
/deep/ .library-content-header__inputs .el-button:focus {
  color: $library-text;
  background-color: $library-soft-control;
  border-color: $library-text;
}

/deep/ .library-content-header__button-filter:hover,
/deep/ .library-content-header__button-filter:focus {
  color: $blue;
  background-color: rgba($blue, 0.08);
  border-color: rgba($blue, 0.45);
}

/deep/ .library-content-header__button-filter--active,
/deep/ .library-content-header__button-filter--active:hover,
/deep/ .library-content-header__button-filter--active:focus {
  color: $white;
  background-color: $blue;
  border-color: $blue;
  box-shadow: $library-filter-button-active-shadow;
}

/deep/ .library-content-header__bulk-toolbar {
  border-top: $library-border-width solid $library-border-color;
}

.library--th /deep/ .el-button:hover,
.library--th /deep/ .el-button:focus {
  color: $library-th-accent;
  border-color: $library-th-accent;
}

.library--th /deep/ .library-content-header__button-filter--active,
.library--th /deep/ .library-content-header__button-filter--active:hover,
.library--th /deep/ .library-content-header__button-filter--active:focus {
  color: $white;
  background-color: $blue;
  border-color: $blue;
}

.main-content__workspace {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background-color: $library-page-background;
}

.main-content__items {
  flex: 1 1 auto;
  min-width: 0;
  height: percentage(100);
  min-height: 0;
}

.library__aside-right-container {
  position: relative;
  z-index: $filter-sidebar-z-index;
  display: flex;
  flex: 0 0 $filter-sidebar-expanded-width;
  flex-direction: column;
  align-self: stretch;
  box-sizing: border-box;
  width: $filter-sidebar-expanded-width;
  min-width: $filter-sidebar-expanded-width;
  height: percentage(100);
  min-height: 0;
  overflow: hidden;
  background-color: $white;
  border-left: $border-divider;
  box-shadow: none;
  transition:
    width $filter-sidebar-transition-duration ease,
    min-width $filter-sidebar-transition-duration ease,
    flex-basis $filter-sidebar-transition-duration ease;

  @media (max-width: $media-query-s) {
    display: none;
  }
}

.library__aside-right-container--collapsed {
  flex-basis: $filter-sidebar-collapsed-width;
  width: $filter-sidebar-collapsed-width;
  min-width: $filter-sidebar-collapsed-width;
}

.library__aside-right-content {
  flex: 1 1 auto;
  box-sizing: border-box;
  width: percentage(100);
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.library__aside-right {
  align-self: flex-start;
  box-sizing: border-box;
  width: percentage(100);
  height: percentage(100);
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  background-color: $white;
  border: 0;
  border-radius: 0;
  box-shadow: none;
}

.library__aside-right-collapsed-item {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: percentage(100);
  min-height: spacing(6);
  margin: spacing(2) 0 spacing(0.25);
  color: $filter-sidebar-toggle-color;
  font-size: spacing(2.5);
  background-color: $filter-sidebar-active-background;
  border-radius: spacing(1) 0 0 spacing(1);

  &::after {
    position: absolute;
    top: spacing(1);
    right: 0;
    bottom: spacing(1);
    width: $filter-sidebar-active-indicator-width;
    background-color: $filter-sidebar-toggle-color;
    border-radius: spacing(0.5) 0 0 spacing(0.5);
    content: '';
  }
}

.library__aside-right-footer {
  display: flex;
  flex: 0 0 $filter-sidebar-footer-height;
  align-items: flex-end;
  justify-content: flex-end;
  box-sizing: border-box;
  width: percentage(100);
  min-height: $filter-sidebar-footer-height;
  padding:
    0 $filter-sidebar-toggle-edge-offset
    $filter-sidebar-toggle-bottom-offset 0;
  background-color: $white;
}

.library__aside-right-toggle {
  position: static;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: $filter-sidebar-toggle-size;
  height: $filter-sidebar-toggle-size;
  margin: 0;
  padding: 0;
  color: $filter-sidebar-toggle-muted-color;
  font-size: 0;
  line-height: 1;
  background-color: transparent;
  border: 0;
  border-radius: spacing(0.5);
  outline: 0;
  box-shadow: none;
  cursor: pointer;
  transition:
    color $transition,
    background-color $transition,
    box-shadow $transition;

  &:hover {
    color: $filter-sidebar-toggle-color;
    background-color: $filter-sidebar-toggle-hover-background;
  }

  &:focus-visible {
    color: $filter-sidebar-toggle-color;
    box-shadow: $filter-sidebar-toggle-focus-shadow;
  }
}

.library__aside-right-container--collapsed .library__aside-right-footer {
  justify-content: center;
  padding-right: 0;
  padding-left: 0;
}

.library__aside-right-toggle .the-aside-left__toggle-icon {
  margin: 0;
  font-size: spacing(2);
  line-height: 1;
}

.library__detail-item-dropdown {
  margin-left: spacing(2);
}

.library__detail-item-dropdown--danger {
  color: $red;

  &:hover {
    color: $red;
  }
}

@media (max-width: $media-query-l) {
  .library-hero__statement {
    display: none;
  }
}

@media (max-width: $media-query-s) {
  .library-hero {
    flex-basis: spacing(17);
    height: spacing(17);
  }

  .library-hero--collapsed {
    flex-basis: 0;
    height: 0;
  }

  .library-hero__content {
    padding-left: spacing(3);
  }

  .library-page-header--compact {
    flex-direction: column;
    align-items: stretch;
  }

  .library-page-header__summary {
    padding: spacing(1.5) spacing(2) 0;
  }

  /deep/ .library-content-header {
    padding-right: spacing(2);
    padding-left: spacing(2);
  }

  .library-page-header--compact /deep/ .library-content-header {
    padding-left: spacing(2);
  }

  .library-hero__title {
    font-size: spacing(5);
  }

  /deep/ .library-content-header__inputs {
    flex-wrap: wrap;
    row-gap: spacing(1.5);
    justify-content: flex-end;
  }

  /deep/ .library-content-header__search {
    flex: 1 1 percentage(100);
    width: percentage(100);
    min-width: 0;
    max-width: none;
    margin-right: 0;
  }

  /deep/ .library-content-header__actions {
    flex: 1 1 percentage(100);
    flex-wrap: wrap;
    justify-content: flex-end;
  }
}
</style>
